import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { positionsApi } from '../../../api/admin/positions';
import { departmentsApi } from '../../../api/admin/departments';
import { ApiError } from '../../../api/client';
import { IconActionButton } from '../../../components/IconActionButton';
import { Power } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable } from '../../../components/DataTable';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

const formSchema = z.object({
  title: z.string().min(2, 'Title must be at least 2 characters.'),
  departmentId: z.string().optional(),
});
type FormValues = z.infer<typeof formSchema>;

export function PositionsPage() {
  const canManage = usePermission('admin.positions.manage');
  const [modalOpen, setModalOpen] = useState(false);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const positionsQuery = useQuery({ queryKey: ['positions'], queryFn: () => positionsApi.list() });
  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: () => departmentsApi.list() });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) =>
      positionsApi.create({ title: values.title, departmentId: values.departmentId ? Number(values.departmentId) : null }),
    onSuccess: () => {
      showSuccess('Position created.');
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      setModalOpen(false);
      reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create position.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => positionsApi.deactivate(id),
    onSuccess: () => {
      showSuccess('Position deactivated.');
      queryClient.invalidateQueries({ queryKey: ['positions'] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate position.'),
  });

  const departmentName = (id: number | null) => departmentsQuery.data?.data.find((d) => d.id === id)?.name ?? '-';

  return (
    <div className="page">
      <PageHeader title="Positions" actions={canManage && <button className="btn-primary" onClick={() => setModalOpen(true)}>New Position</button>} />

      {positionsQuery.isLoading && <p>Loading…</p>}
      {positionsQuery.isError && <p className="error-text">Could not load positions.</p>}

      {positionsQuery.data && (
        <DataTable
          rows={positionsQuery.data.data}
          rowKey={(p) => p.id}
          emptyMessage="No positions yet."
          columns={[
            { header: 'Title', render: (p) => p.title },
            { header: 'Department', render: (p) => departmentName(p.department_id) },
            { header: 'Status', render: (p) => (p.is_active ? 'Active' : 'Inactive') },
            {
              header: '',
              render: (p) =>
                canManage && p.is_active ? (
                  <IconActionButton icon={Power} label="Deactivate" onClick={() => deactivateMutation.mutate(p.id)} />
                ) : null,
            },
          ]}
        />
      )}

      {modalOpen && (
        <Modal title="New Position" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Title
              <input {...register('title')} />
              {errors.title && <span className="field-error">{errors.title.message}</span>}
            </label>
            <label>
              Department (optional)
              <select {...register('departmentId')}>
                <option value="">- none -</option>
                {departmentsQuery.data?.data.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Create'}
              </button>
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
